import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { RejectDialogPaper, RejectDialogText } from './RejectDialog.styles';

interface RejectDialogProps {
  open: boolean;
  variant?: 'leave' | 'default';
  onClose: () => void;
  onConfirm: (note: string) => void;
  isLoading?: boolean;
}

const MIN_NOTE_LENGTH = 4;

const RejectDialog = ({
  open,
  variant = 'default',
  onClose,
  onConfirm,
  isLoading
}: RejectDialogProps) => {
  const { t } = useTranslation('common');
  const [note, setNote] = useState('');
  const isValid = note.trim().length >= MIN_NOTE_LENGTH;

  const prefix =
    variant === 'leave'
      ? 'plannerMessages.rejectDialogLeave'
      : 'plannerMessages.rejectDialog';

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(note.trim());
      setNote('');
    }
  };

  const handleClose = () => {
    setNote('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{ paper: { component: RejectDialogPaper } }}
    >
      <DialogTitle>{t(`${prefix}.title`)}</DialogTitle>
      <DialogContent>
        <RejectDialogText>{t(`${prefix}.text`)}</RejectDialogText>
        <TextField
          autoFocus
          fullWidth
          multiline
          maxRows={6}
          label={t(`${prefix}.noteLabel`)}
          placeholder={t(`${prefix}.notePlaceholder`)}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={note.length > 0 && !isValid}
          helperText={
            note.length > 0 && !isValid
              ? t(`${prefix}.noteMinLength`, {
                  min: MIN_NOTE_LENGTH
                })
              : undefined
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t(`${prefix}.cancel`)}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={!isValid || isLoading}
        >
          {t(`${prefix}.reject`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectDialog;
